"use strict";
/**
 * Shared PM-US → Kalshi-shape adapters for the market-selection modals
 * (additional-markets and parallel-markets).
 *
 * The modals render Kalshi `Event` / `MarketFromEvent` lists; these
 * adapters coerce PM-US API payloads into those shapes so one rendering
 * path serves both venues.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PM_US_EVENTS_LIMIT = void 0;
exports.pmUsEventToEvent = pmUsEventToEvent;
exports.pmUsMarketToMarketFromEvent = pmUsMarketToMarketFromEvent;
exports.pmUsSportEntrySlug = pmUsSportEntrySlug;
exports.normalizeSportsResponse = normalizeSportsResponse;
exports.normalizePmUsEventsResponse = normalizePmUsEventsResponse;
const polymarket_us_fetcher_1 = require("../../../markets/polymarket-us-fetcher");
/**
 * Max PM-US events to request per sport search in the market-selection modals.
 *
 * This is no longer a payload guard. It used to be 18 for that reason and it
 * did not work — the /events payload embedded every event's nested markets, so
 * 18 MLB events was still 12 MB and loads failed anyway. The server now strips
 * nested markets from the list, which costs ~1 KB per event; 40 events is well
 * under a kilobyte on the wire once gzipped.
 *
 * What this number now controls is how far ahead the list reaches. The server
 * asks for events soonest-first, so this is "the next 40 events in the league"
 * — comfortably more than a full day's slate in any sport, while keeping the
 * list short enough to scan. The upstream search is separately capped at ~3
 * days out (EVENTS_END_DATE_MAX_DAYS), which is the real near-term filter.
 */
exports.PM_US_EVENTS_LIMIT = 15; // perhaps lower so we don't fetch more games than needed and risk the user selecting the wrong one?
/** Thin adapter: PM-US event → Kalshi Event shape used by the modal list. */
function pmUsEventToEvent(e) {
    return {
        event_ticker: String(e.id ?? e.slug ?? ""),
        title: String(e.title ?? e.name ?? ""),
        category: "pm-us",
        sub_title: "",
    };
}
/** Thin adapter: PM-US market → MarketFromEvent shape used by the modal list. */
function pmUsMarketToMarketFromEvent(m, eventTicker) {
    // YES is the long side, as on the primary; a leg's side toggle names them.
    const sides = (0, polymarket_us_fetcher_1.parseMarketSides)(m);
    return {
        ...(sides && sides.length >= 2 ? { sideNames: [sides[0], sides[1]] } : {}),
        ticker: String(m.slug ?? m.id ?? ""),
        event_ticker: eventTicker,
        // `titleShort` ("LAD -1.5 F5") is what the modal list wants: the
        // renderer falls back to the raw slug for titles over 50 chars, which
        // is what always happened when this read the resolution paragraph.
        title: String(m.titleShort ?? m.title ?? m.question ?? ""),
        subtitle: String(m.market_type ?? m.marketType ?? ""),
        ...(0, polymarket_us_fetcher_1.pmUsMarketCategory)(m),
        status: m.closed
            ? "finalized"
            : m.active === false
                ? "inactive"
                : "open",
        yes_bid_dollars: "0",
        yes_ask_dollars: String(m.bestAsk ?? 0),
        no_bid_dollars: "0",
        no_ask_dollars: "0",
        last_price_dollars: String(m.lastTradePrice ?? 0),
        volume_fp: "0",
        open_interest_fp: "0",
        expiration_time: "",
    };
}
/**
 * The slug/tag value for a PM-US sport entry — the value used both as a
 * preset button's search key and to match a resolved default league. Kept in
 * one place so the fallback order (`slug → sport → name`) can't drift between
 * where chips are built and where a league is validated.
 */
function pmUsSportEntrySlug(entry) {
    return String(entry.slug ?? entry.sport ?? entry.name ?? "");
}
/** Normalise PM-US sports response which may be an array or `{ sports: [] }`. */
function normalizeSportsResponse(payload) {
    if (Array.isArray(payload))
        return payload;
    if (payload && Array.isArray(payload.sports))
        return payload.sports;
    return [];
}
/** Normalise PM-US events response which may be an array or `{ events: [] }`. */
function normalizePmUsEventsResponse(data) {
    if (Array.isArray(data))
        return data;
    if (data &&
        Array.isArray(data.events)) {
        return data.events ?? [];
    }
    return [];
}
